import { ActiveCallHeader } from '@/components/agent/ActiveCallHeader';
import { CallEndedDialog } from '@/components/agent/CallEndedDialog';
import { ConversationArea } from '@/components/agent/ConversationArea';
import { ErrorDialog } from '@/components/agent/ErrorDialog';
import { FollowupDialog } from '@/components/agent/FollowupDialog';
import { IncomingCallCard } from '@/components/agent/IncomingCallCard';
import { ModernIncomingCall } from '@/components/agent/ModernIncomingCall';
import { ModernConversationArea } from '@/components/agent/ModernConversationArea';
import { ModernActiveCallHeader } from '@/components/agent/ModernActiveCallHeader';
import { ThankYouDialog } from '@/components/agent/ThankYouDialog';
import {
  AccentType,
  CallState,
  LoadingState,
  useAgentCall,
} from '@/hooks/useAgentCall';
import { useCoachStore } from '@/store/coach';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export enum CallType {
  INITIAL = 'initial',
  FOLLOWUP = 'followup',
}

export enum CallMode {
  NORMAL = 'normal',
  SCRIPTED = 'scripted',
}

// Create theme with modern branding
const theme = createTheme({
  palette: {
    primary: {
      main: '#0066cc', // Professional blue
      light: '#4d94ff',
      dark: '#004c99',
    },
    error: {
      main: '#F44336', // Modern red for end call button
      light: '#EF5350',
      dark: '#D32F2F',
    },
    success: {
      main: '#4caf50',
    },
    warning: {
      main: '#FF9800', // Orange for scripted mode
      light: '#FFB74D',
      dark: '#F57C00',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

// Helper to parse accent from URL
const getAccentFromParams = (accentParam: string | null): AccentType => {
  if (!accentParam) return AccentType.DEFAULT;

  switch (accentParam.toLowerCase()) {
    case 'british':
      return AccentType.BRITISH;
    case 'irish':
      return AccentType.IRISH;
    default:
      return AccentType.DEFAULT;
  }
};

export function AgentRolePlay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productCode = searchParams.get('product') || 'prushield';
  const callType =
    (searchParams.get('callType') as CallType) || CallType.INITIAL;
  const parentId = searchParams.get('parentId');
  const accentParam = searchParams.get('accent');
  const accent = getAccentFromParams(accentParam);
  const mode = (searchParams.get('mode') as CallMode) || CallMode.NORMAL;
  const scriptId = searchParams.get('scriptId') || 'prudential-annual-checkin';
  const coachStore = useCoachStore();

  // Local state
  const [showFollowupModal, setShowFollowupModal] = useState<boolean>(false);
  const [showCallEndedDialog, setShowCallEndedDialog] =
    useState<boolean>(false);
  const [showThankYouDialog, setShowThankYouDialog] = useState<boolean>(false);

  // Initialize agent call hook with accent and mode
  const {
    loadingState,
    error,
    callState,
    sessionId,
    agentInfo,
    clientInfo,
    product,
    isMuted,
    callDuration,
    transcript,
    isSpeaking,
    handleAnswerCall,
    handleEndCall,
    setClientInfo,
    saveClientInfo,
    formatTime,
    retryInitialization,
  } = useAgentCall({
    productCode,
    callType,
    accent,
    mode,
    scriptId,
    parentId,
    userId: coachStore.userId,
    onClientInfoNeeded: () => {
      if (mode === CallMode.SCRIPTED) {
        setShowThankYouDialog(true);
      } else {
        setShowFollowupModal(true);
      }
    },
  });

  // Handle declining the call
  const handleDeclineCall = () => {
    // For scripted mode, reload the same page instead of going to /agent
    if (mode === CallMode.SCRIPTED) {
      window.location.reload();
    } else {
      navigate('/agent');
    }
  };

  // Handle saving client info and generating follow-up link
  const handleSaveClientInfo = async (
    clientInfoData: any,
    followupDate: string,
    followupTime: string,
    followupNotes: string,
  ) => {
    const followupUrl = await saveClientInfo(
      clientInfoData,
      followupDate,
      followupTime,
      followupNotes,
    );

    // After saving, close the modal after a delay
    setTimeout(() => {
      setShowFollowupModal(false);
      setShowCallEndedDialog(true);

      // Navigate after showing the call ended dialog
      setTimeout(() => {
        navigate('/agent');
      }, 5000);
    }, 3000);

    return followupUrl;
  };

  // Check if we should show error dialog
  const showErrorDialog = error !== null && loadingState === LoadingState.ERROR;

  // Helper function to get the accent name for display
  const getAccentDisplay = () => {
    switch (accent) {
      case AccentType.BRITISH:
        return 'British';
      case AccentType.IRISH:
        return 'Irish';
      default:
        return 'Default';
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          bgcolor: mode === CallMode.SCRIPTED && callState === CallState.ACTIVE ? '#FAFAFA' : 'grey.100',
        }}
      >
        {/* Incoming Call UI */}
        {callState === CallState.INCOMING && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: mode === CallMode.SCRIPTED ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(8px)',
              zIndex: 1300,
              p: 2,
            }}
          >
            {mode === CallMode.SCRIPTED ? (
              <ModernIncomingCall
                agentInfo={agentInfo}
                callType={callType}
                callMode={mode}
                scriptId={scriptId}
                onAnswer={handleAnswerCall}
                onDecline={handleDeclineCall}
              />
            ) : (
              <IncomingCallCard
                agentInfo={agentInfo}
                callType={callType}
                callMode={mode}
                onAnswer={handleAnswerCall}
                onDecline={handleDeclineCall}
                accentDisplay={
                  accent !== AccentType.DEFAULT ? getAccentDisplay() : undefined
                }
              />
            )}
          </Box>
        )}

        {/* Active Call UI - Show even when ENDED in scripted mode to keep transcript visible */}
        {(callState === CallState.ACTIVE || (callState === CallState.ENDED && mode === CallMode.SCRIPTED)) && (
          <>
            {mode === CallMode.SCRIPTED ? (
              <ModernActiveCallHeader
                agentInfo={agentInfo}
                callType={callType}
                callMode={mode}
                scriptId={scriptId}
                callDuration={formatTime(callDuration)}
                isSpeaking={isSpeaking}
                onEndCall={handleEndCall}
              />
            ) : (
              <ActiveCallHeader
                agentInfo={agentInfo}
                callType={callType}
                callMode={mode}
                callDuration={formatTime(callDuration)}
                isSpeaking={isSpeaking}
                onEndCall={handleEndCall}
                accentDisplay={
                  accent !== AccentType.DEFAULT ? getAccentDisplay() : undefined
                }
              />
            )}

            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              {mode === CallMode.SCRIPTED ? (
                <ModernConversationArea
                  transcript={transcript}
                  isSpeaking={isSpeaking}
                  agentInfo={agentInfo}
                />
              ) : (
                <ConversationArea
                  transcript={transcript}
                  isSpeaking={isSpeaking}
                />
              )}
            </Box>

            {/* Progress tracker removed for streamlined scripted calls */}
          </>
        )}

        {/* Follow-up Dialog */}
        <FollowupDialog
          open={showFollowupModal}
          onClose={() => setShowFollowupModal(false)}
          clientInfo={clientInfo}
          onSave={handleSaveClientInfo}
        />

        {/* Call Ended Dialog */}
        <CallEndedDialog
          open={showCallEndedDialog && !showFollowupModal}
          onClose={() => setShowCallEndedDialog(false)}
          agentName={agentInfo?.profile?.name || 'the advisor'}
          callDuration={formatTime(callDuration)}
        />

        {/* Error Dialog */}
        <ErrorDialog
          open={showErrorDialog}
          error={error}
          onRetry={retryInitialization}
          onClose={() => {
            if (mode === CallMode.SCRIPTED) {
              window.location.reload();
            } else {
              navigate('/agent');
            }
          }}
        />

        {/* Thank You Dialog for Scripted Mode */}
        <ThankYouDialog
          open={showThankYouDialog}
          onClose={() => setShowThankYouDialog(false)}
          onTryAgain={() => window.location.reload()}
          agentName={agentInfo?.profile?.name || 'Rachel'}
          agentAvatar={agentInfo?.profile?.avatar || 'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp'}
        />
      </Box>
    </ThemeProvider>
  );
}
